import {
  AndroidLogoIcon,
  AppleLogoIcon,
  CheckIcon,
  ClockCounterClockwiseIcon,
  CursorIcon,
  GithubLogoIcon,
  LockSimpleIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { useCopy } from 'src/hooks';

import { Api } from '../../../api';
import { RouterLink, Text } from '../../../components';
import { Color, Style } from '../../../constants';
import TicketBadge from '../../client-tickets/components/TicketBadge';
import CardActionsMenu from './CardActionsMenu';
import CommitAuthorAvatar from './CommitAuthorAvatar';
import CopyableText from './CopyableText';

type Deployment = Api.Environment.V['deployments'][number];

type Props = {
  deployment: Deployment;
  companySlug: string;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onViewCommitHistory: () => void;
};

// Prefer the explicit build platform; fall back to the name for non-app
// deployments (web / API).
export function deploymentPlatform(
  deployment: Deployment
): 'ios' | 'android' | null {
  if (deployment.buildPlatform === 'ios' || /^ios\b/i.test(deployment.name))
    return 'ios';
  if (
    deployment.buildPlatform === 'android' ||
    /^android\b/i.test(deployment.name)
  )
    return 'android';
  return null;
}

function deploymentIcon(deployment: Deployment) {
  const platform = deploymentPlatform(deployment);
  if (platform === 'ios') return AppleLogoIcon;
  if (platform === 'android') return AndroidLogoIcon;
  return CursorIcon;
}

// App store status from a build event — icons and wording mirror the release
// page (client-releases/BuildEventDetail).
function appEventStatus(type: string): {
  label: string;
  iconClass: string;
  color: string;
} {
  switch (type) {
    case 'live':
      return {
        label: 'Live',
        iconClass: 'fa-regular fa-circle-check',
        color: Color.green,
      };
    case 'rejected':
      return {
        label: 'Rejected by review',
        iconClass: 'fa-solid fa-ban',
        color: Color.accent,
      };
    case 'awaiting_review':
      return {
        label: 'Awaiting review',
        iconClass: 'fa-regular fa-clock',
        color: Color.blue,
      };
    case 'rollout_paused':
      return {
        label: 'Rollout paused',
        iconClass: 'fa-regular fa-circle-pause',
        color: Color.blue,
      };
    case 'rollout_started':
      return {
        label: 'Automatic rollout started',
        iconClass: 'fa-solid fa-circle-half-stroke',
        color: Color.blue,
      };
    case 'rollout_resumed':
      return {
        label: 'Automatic rollout resumed',
        iconClass: 'fa-solid fa-circle-half-stroke',
        color: Color.blue,
      };
    case 'rollout_increased':
      return {
        label: 'Rollout increased',
        iconClass: 'fa-solid fa-circle-half-stroke',
        color: Color.blue,
      };
    default:
      return {
        label: type,
        iconClass: 'fa-solid fa-clock-rotate-left',
        color: Color.neutral60,
      };
  }
}

export default function DeploymentCard({
  deployment,
  companySlug,
  canManage,
  onEdit,
  onDelete,
  onViewCommitHistory,
}: Props) {
  const { copiedKey, copy } = useCopy();

  const commitDate = deployment.lastCommitTimestamp
    ? format(parseISO(deployment.lastCommitTimestamp), 'd MMM yyyy')
    : null;

  const DeploymentIcon = deploymentIcon(deployment);

  // App store status comes from the latest build event on the linked build.
  const buildEvents = deployment.latestBuild?.buildEvents ?? [];
  const latestEvent = buildEvents.length
    ? buildEvents.reduce((a, b) => (b.recordedAt > a.recordedAt ? b : a))
    : null;
  const appStatus = latestEvent ? appEventStatus(latestEvent.type) : null;
  const appStatusDate = latestEvent
    ? format(parseISO(latestEvent.recordedAt), 'd MMM yyyy')
    : null;

  const latestBuild = deployment.latestBuild;
  const onTestFlight = latestBuild?.link === 'testflight';
  // Version + build after the name, matching the design's "iOS 1.2.1 (2)".
  const buildLabel = latestBuild
    ? ` ${latestBuild.release.versionNumber} (${latestBuild.buildNumber})`
    : '';
  // App deployments link to the internal Release page; web/admin link out to
  // their own URL.
  const releasePath = latestBuild
    ? `/company/${companySlug}/releases/${latestBuild.release.versionNumber}`
    : null;

  return (
    <html.div style={styles.card}>
      <html.div style={styles.header}>
        <html.div style={styles.headerMain}>
          <html.div style={[Style.row, Style.alignCenter, styles.titleRow]}>
            <DeploymentIcon size={18} color={Color.neutral80} />
            {releasePath ? (
              <RouterLink to={releasePath}>
                <Text typeface="body.medium" color="neutral100" underline>
                  {deployment.name}
                  {buildLabel}
                </Text>
              </RouterLink>
            ) : deployment.url ? (
              <html.a
                href={deployment.url}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.titleLink}
              >
                <Text typeface="body.medium" color="neutral100" underline>
                  {deployment.name}
                  {buildLabel}
                </Text>
              </html.a>
            ) : (
              <Text typeface="body.medium" color="neutral100">
                {deployment.name}
                {buildLabel}
              </Text>
            )}
            {onTestFlight && (
              <TicketBadge
                label="On TestFlight"
                bgColor={Color.turquoiseBg}
                textColor={Color.turquoise}
              />
            )}
          </html.div>

          {appStatus && (
            <html.div style={[Style.row, Style.alignCenter, styles.statusRow]}>
              <i
                className={appStatus.iconClass}
                style={{
                  fontSize: 16,
                  width: 18,
                  textAlign: 'center',
                  color: appStatus.color,
                }}
              />
              <Text typeface="body.small.regular" color="neutral100">
                {appStatus.label}
              </Text>
              {appStatusDate && (
                <Text typeface="body.small.regular" color="neutral60">
                  {appStatusDate}
                </Text>
              )}
            </html.div>
          )}

          {deployment.visitorPassword && (
            <html.div
              style={[Style.row, Style.alignCenter, styles.passwordRow]}
            >
              <LockSimpleIcon size={18} color={Color.neutral60} />
              <Text typeface="body.small.regular" color="neutral60">
                Visitor password
              </Text>
              <CopyableText
                value={deployment.visitorPassword}
                label="Copy visitor password"
                copied={copiedKey === 'vp'}
                onCopy={() => copy('vp', deployment.visitorPassword!)}
                typeface="body.small.medium"
              />
            </html.div>
          )}
        </html.div>

        {canManage && (
          <CardActionsMenu
            items={[
              {
                key: 'history',
                label: 'View commit history',
                icon: ClockCounterClockwiseIcon,
                onClick: onViewCommitHistory,
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: PencilSimpleIcon,
                onClick: onEdit,
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: TrashIcon,
                danger: true,
                onClick: onDelete,
              },
            ]}
          />
        )}
      </html.div>

      {/* GitHub repo & branch only show for M10c (agency) members */}
      {canManage && deployment.githubRepo && (
        <html.div style={[Style.row, Style.alignCenter, styles.commitRow]}>
          <GithubLogoIcon size={18} color={Color.neutral60} />
          <Text typeface="body.small.regular" color="neutral60">
            {deployment.githubRepo}
            {deployment.githubBranch && ` / ${deployment.githubBranch}`}
          </Text>
          {deployment.lastCommitAuthor && (
            <CommitAuthorAvatar author={deployment.lastCommitAuthor} />
          )}
          {deployment.lastCommitTitle && (
            <Text typeface="body.small.medium" color="neutral100">
              {deployment.lastCommitTitle}
            </Text>
          )}
          {commitDate && (
            <Text typeface="body.small.regular" color="neutral60">
              {commitDate}
            </Text>
          )}
          {deployment.lastCommitCheckStatus === 'success' && (
            <CheckIcon size={16} color={Color.green} />
          )}
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  card: {
    backgroundColor: color.neutral0,
    borderRadius: 8,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingBlock: 16,
    paddingInline: 16,
  },
  headerMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  titleRow: {
    gap: 8,
  },
  statusRow: {
    gap: 8,
  },
  titleLink: {
    color: 'inherit',
    textDecorationLine: 'none',
  },
  passwordRow: {
    gap: 8,
  },
  commitRow: {
    gap: 8,
    flexWrap: 'wrap',
    paddingBlock: 10,
    paddingInline: 16,
    backgroundColor: color.neutral10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
});
